/**
 * The two calls behind "my sending limits".
 *
 * Both endpoints are about the person holding the session and nobody else, so
 * neither takes a person id. The server reads the signed-in person off the
 * cookie and writes for them alone. That is not a convenience, it is the whole
 * safety property of the feature: there is no field on either call that could
 * name somebody else, so there is nothing for a mistake here to widen.
 *
 * The write can name one of that person's own sending accounts, which is the
 * one field on it that points at something other than the person. It does not
 * weaken the property above, because the server takes the account from the
 * person's own accounts and refuses anything else, and because a number set on
 * an account can only ever hold that account lower than the person already is.
 *
 * The wire shapes live in `my-limits-model.ts` beside the decisions that read
 * them, so the pure module and its tests never have to import react-query to
 * know what a row looks like. They are re-exported here because the page
 * imports its types from the file it imports its hooks from.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { api, qk } from '../../lib'
import type { MyLimit, MyLimitWrite } from './my-limits-model'

export type {
  GovernedNumbers,
  LimitSource,
  MyLimit,
  MyLimitAccount,
  MyLimitWrite,
} from './my-limits-model'

export interface MyLimitsResponse {
  items: MyLimit[]
}

/** The root key, so one write sweeps the list however it was asked for. */
const MY_LIMITS_ROOT = ['my-limits'] as const

export function useMyLimits(): UseQueryResult<MyLimitsResponse> {
  return useQuery({
    queryKey: MY_LIMITS_ROOT,
    queryFn: ({ signal }) => api.get<MyLimitsResponse>('/me/limits', { signal }),
    // Short, because what is left of today moves while somebody is reading it.
    staleTime: 15_000,
  })
}

/**
 * Set one number for this person or for one of their sending accounts, or clear
 * it.
 *
 * The answer is the row as it now resolves, which is not always the row that
 * was asked for: a sending account can be held lower than the person set, and
 * the resolved numbers reflect that. The list is invalidated rather than patched
 * from the answer so every row is re-read against the same resolution the server
 * just performed.
 *
 * Two other reads move when this one is written. The day's meters on the
 * dashboard are drawn against these allowances, and the admin table lists the
 * row this write creates or removes, so both are swept as well. Leaving either
 * stale would show somebody a number the platform has already stopped using.
 */
export function useSetMyLimit(): UseMutationResult<MyLimit, unknown, MyLimitWrite> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: MyLimitWrite) => api.put<MyLimit>('/me/limits', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: MY_LIMITS_ROOT })
      void client.invalidateQueries({ queryKey: ['analytics', 'quota'] })
      void client.invalidateQueries({ queryKey: qk.policies() })
    },
  })
}
